/**
 * Opportunity Activities Panel
 *
 * Lightweight per-opportunity activity log with quick-add buttons for
 * note / call / email / meeting / task. Used inside OpportunityDetailPanel.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Edit,
  Phone,
  Mail,
  Calendar as CalendarIcon,
  ListChecks,
  Loader2,
  Plus,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { activitiesAPI, Activity } from '@/services/api/salesPipelineAPI';

interface OpportunityActivitiesPanelProps {
  opportunityId: string;
  contactName?: string;
}

type QuickAddType = 'note' | 'call' | 'email' | 'meeting' | 'task';

const TYPE_META: Record<QuickAddType, { label: string; icon: React.ReactNode; color: string }> = {
  note:    { label: 'Note',    icon: <Edit className="h-3.5 w-3.5" />,         color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100' },
  call:    { label: 'Call',    icon: <Phone className="h-3.5 w-3.5" />,        color: 'bg-green-50 text-green-700 hover:bg-green-100' },
  email:   { label: 'Email',   icon: <Mail className="h-3.5 w-3.5" />,         color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
  meeting: { label: 'Meeting', icon: <CalendarIcon className="h-3.5 w-3.5" />, color: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
  task:    { label: 'Task',    icon: <ListChecks className="h-3.5 w-3.5" />,   color: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
};

export const OpportunityActivitiesPanel: React.FC<OpportunityActivitiesPanelProps> = ({
  opportunityId,
  contactName,
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [composeType, setComposeType] = useState<QuickAddType | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await activitiesAPI.getActivities({ opportunityId });
      setActivities(list);
    } catch (e) {
      console.error('Failed to load opportunity activities', e);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => { load(); }, [load]);

  const startCompose = (type: QuickAddType) => {
    setComposeType(type);
    setTitle('');
    setDescription('');
  };

  const cancelCompose = () => {
    setComposeType(null);
    setTitle('');
    setDescription('');
  };

  const submit = async () => {
    if (!composeType) return;
    if (!title.trim() && !description.trim()) {
      cancelCompose();
      return;
    }
    setSaving(true);
    try {
      await activitiesAPI.createActivity({
        type: composeType,
        title: title.trim() || TYPE_META[composeType].label,
        description: description.trim() || undefined,
        opportunityId,
        contact: contactName || '',
        completed: composeType === 'note',
        priority: 'medium',
        dueDate: new Date().toISOString().split('T')[0],
        activityDate: new Date().toISOString(),
      } as any);
      cancelCompose();
      await load();
    } catch (e: any) {
      console.error('Failed to create activity', e);
      alert(`Failed to log activity: ${e?.response?.data?.detail || e?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async (act: Activity) => {
    try {
      await activitiesAPI.completeActivity(act.id);
      await load();
    } catch (e) {
      console.error('Failed to complete activity', e);
    }
  };

  const remove = async (act: Activity) => {
    if (!confirm('Delete this activity?')) return;
    try {
      await activitiesAPI.deleteActivity(act.id);
      await load();
    } catch (e) {
      console.error('Failed to delete activity', e);
    }
  };

  return (
    <div className="space-y-4">
      {/* Quick add buttons */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Log activity</h3>
          {composeType && (
            <button onClick={cancelCompose} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(TYPE_META) as QuickAddType[]).map((t) => (
            <button
              key={t}
              onClick={() => startCompose(t)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-transparent transition-colors ${
                composeType === t ? 'ring-2 ring-blue-400 ' : ''
              }${TYPE_META[t].color}`}
            >
              <Plus className="h-3 w-3" />
              {TYPE_META[t].icon}
              {TYPE_META[t].label}
            </button>
          ))}
        </div>
      </div>

      {/* Compose form (inline) */}
      {composeType && (
        <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`${TYPE_META[composeType].label} title`}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Notes, agenda, summary..."
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={cancelCompose}
              className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Save {TYPE_META[composeType].label}
            </button>
          </div>
        </div>
      )}

      {/* Activity history */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">History</h3>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading activities...
          </div>
        ) : activities.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6 border rounded-lg bg-gray-50">
            No activities yet. Use the buttons above to log your first one.
          </p>
        ) : (
          <ul className="space-y-2">
            {activities.map((a) => {
              const meta = TYPE_META[(a.type as QuickAddType)] || TYPE_META.note;
              return (
                <li key={a.id} className="border rounded-lg p-3 bg-white">
                  <div className="flex items-start gap-2">
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${meta.color.replace('hover:bg-', 'bg-')}`}>
                      {meta.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${a.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                          {a.title || meta.label}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {!a.completed && a.type !== 'note' && (
                            <button
                              onClick={() => markComplete(a)}
                              className="p-1 text-gray-400 hover:text-green-600"
                              title="Mark complete"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => remove(a)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {a.description && (
                        <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{a.description}</p>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">
                        {a.activityDate ? new Date(a.activityDate).toLocaleString() : a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
